/* TODO(ASIMO-2105): cherrera2@ to remove once experiment is over regardless of outcome */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl-next';

import { type AnalyticsEventPayload, useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Link from '@atlaskit/link';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import {
	ANALYTICS_CHANNEL,
	buildAuthClickedEventPayload,
	buildAuthCompletedEventPayload,
	buildAuthFailedEventPayload,
	ThirdPartyAuthSource,
	withAnalyticsContext,
} from '../../services/analytics';
import { useBrowserStorage } from '../../services/hooks';
import {
	DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
	GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
	ONE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
	type ThirdPartyConnectionStatus,
} from '../../services/storage';
import {
	THIRD_PARTY_OAUTH_SUCCESS_EVENT,
	triggerThirdPartyAuthFlow,
} from '../../services/thirdPartyAuth';

import { GoogleDriveSvg, OneDriveSvg } from './no-results-third-party-svg';

export const messages = defineMessages({
	noResultsThirdPartyGoogleDriveHeader: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.googleDriveHeading',
		defaultMessage: 'Connect your Google account',
		description:
			'Heading message shown when there are no results for Google Drive, but the search form is empty',
	},
	noResultsThirdPartyGoogleDriveDescription: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.googleDriveDescription',
		defaultMessage:
			'Connect your Google account to make it easier to find, share, and collaborate on work.',
		description:
			'The description message shown when there are no results for Google Drive and the user is not authorized',
	},
	noResultsThirdPartyGoogleDriveAuthorizedHeader: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.googleDriveAuthorizedHeading',
		defaultMessage: 'Google Drive is connected to your account',
		description:
			'Heading message shown when there are no results for Google Drive and the user is authorized',
	},
	noResultsThirdPartyGoogleDrivePrimaryAction: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.googleDrivePrimaryAction',
		defaultMessage: 'Connect to Google',
		description: 'Describes the primary action of connecting to Google',
	},
	noResultsThirdPartyOneDriveHeader: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.oneDriveHeading',
		defaultMessage: 'Connect your OneDrive account',
		description:
			'Heading message shown when there are no results for OneDrive, but the search form is empty',
	},
	noResultsThirdPartyOneDriveDescription: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.oneDriveDescription',
		defaultMessage:
			'Connect your OneDrive account to make it easier to find, share, and collaborate on work.',
		description:
			'The description message shown when there are no results for OneDrive and the user is not authorized',
	},
	noResultsThirdPartyOneDriveAuthorizedHeader: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.oneDriveAuthorizedHeading',
		defaultMessage: 'OneDrive is connected to your account',
		description:
			'Heading message shown when there are no results for OneDrive and the user is authorized',
	},
	noResultsThirdPartyOneDrivePrimaryAction: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.oneDrivePrimaryAction',
		defaultMessage: 'Connect to OneDrive',
		description: 'Describes the primary action of connecting to OneDrive',
	},
	noResultsThirdPartyAuthorizedDescription: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.authorizedDescription',
		defaultMessage: 'As you view and edit files, they will appear here.',
		description:
			'The description message shown when there are no results for the third party source and the user is not authorized',
	},
	noResultsThirdPartyDescriptionLink: {
		id: 'fabric.linkPickerAtlassianPlugin.emptyStateNoResultsThirdParty.descriptionLink',
		defaultMessage: 'Read more about Smart Links.',
		description: 'Describes the link to the Smart Links documentation',
	},
});

export const DESCRIPTION_URL =
	'https://support.atlassian.com/confluence-cloud/docs/what-data-is-sent-and-received-when-pasting-a-smart-link';

export const TEST_IDS = {
	emptyStateNoResultsThirdParty: 'link-picker-empty-state-no-results-third-party',
	emptyStateNoResultsThirdPartyDescription:
		'link-picker-empty-state-no-results-third-party-description',
	emptyStateNoResultsThirdPartyDescriptionLink:
		'link-picker-empty-state-no-results-third-party-description-link',
	emptyStateNoResultsThirdPartyPrimaryAction:
		'link-picker-empty-state-no-results-third-party-primary-action',
};

const styles = cssMap({
	emptyStateThirdPartyWrapperStyles: {
		textAlign: 'center',
		maxWidth: '304px',
		display: 'flex',
		paddingBlockStart: token('space.300', '24px'),
		paddingBlockEnd: token('space.0', '0px'),
		paddingInlineStart: token('space.600', '48px'),
		paddingInlineEnd: token('space.600', '48px'),
		flexDirection: 'column',
		alignItems: 'center',
	},
	headingContainer: {
		paddingBlockEnd: token('space.200', '16px'),
	},
	descriptionContainer: {
		paddingBlockEnd: token('space.200', '16px'),
	},
	actionContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

export enum ThirdPartySource {
	GOOGLE_DRIVE = 'google_drive',
	ONEDRIVE = 'onedrive',
}

interface ThirdPartySourceMapping {
	authorizedHeadingDescriptor: MessageDescriptor;
	connectionStatusStorageKey: string;
	descriptionDescriptor: MessageDescriptor;
	headingDescriptor: MessageDescriptor;
	icon: React.ReactNode;
	primaryActionDescriptor: MessageDescriptor;
	tab: string;
}

const ThirdPartySourceMapping: Record<ThirdPartySource, ThirdPartySourceMapping> = {
	[ThirdPartySource.GOOGLE_DRIVE]: {
		authorizedHeadingDescriptor: messages.noResultsThirdPartyGoogleDriveAuthorizedHeader,
		headingDescriptor: messages.noResultsThirdPartyGoogleDriveHeader,
		descriptionDescriptor: messages.noResultsThirdPartyGoogleDriveDescription,
		primaryActionDescriptor: messages.noResultsThirdPartyGoogleDrivePrimaryAction,
		connectionStatusStorageKey: GOOGLE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
		icon: <GoogleDriveSvg />,
		tab: 'google_drive',
	},
	[ThirdPartySource.ONEDRIVE]: {
		authorizedHeadingDescriptor: messages.noResultsThirdPartyOneDriveAuthorizedHeader,
		headingDescriptor: messages.noResultsThirdPartyOneDriveHeader,
		descriptionDescriptor: messages.noResultsThirdPartyOneDriveDescription,
		primaryActionDescriptor: messages.noResultsThirdPartyOneDrivePrimaryAction,
		connectionStatusStorageKey: ONE_DRIVE_CONNECTION_STATUS_STORAGE_KEY,
		icon: <OneDriveSvg />,
		tab: 'onedrive',
	},
};

/// Component displayed in link picker when there are no results for third party connector tabs or if the
/// user has not connected their account to the corresponding third party connector.
const EmptyStateNoResultsThirdParty: React.FC<{ source: ThirdPartySource }> = ({ source }) => {
	const {
		authorizedHeadingDescriptor,
		headingDescriptor,
		descriptionDescriptor,
		primaryActionDescriptor,
		connectionStatusStorageKey,
		icon,
		tab,
	} = ThirdPartySourceMapping[source];
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const [integrationDetails, setIntegrationDetails] =
		useBrowserStorage<ThirdPartyConnectionStatus | null>(connectionStatusStorageKey, null);
	const hasAuthorized = integrationDetails?.isUserOAuthed || false;
	const hasIntegrationDetails = Object.keys(integrationDetails || {}).length > 0;

	const header = intl.formatMessage(
		hasAuthorized ? authorizedHeadingDescriptor : headingDescriptor,
	);
	const description = intl.formatMessage(
		hasAuthorized ? messages.noResultsThirdPartyAuthorizedDescription : descriptionDescriptor,
	);
	const descriptionLink = intl.formatMessage(messages.noResultsThirdPartyDescriptionLink);
	const primaryAction = intl.formatMessage(primaryActionDescriptor);

	const handleBroadcastedOAuthSuccess = (_: Event) => {
		setIntegrationDetails({
			...integrationDetails!,
			isUserOAuthed: true,
			expirationDate: Date.now() + DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
		});
	};

	// If the user has authorized through the the banner and the empty state is rendered, update the UI state.
	useEffect(() => {
		window.addEventListener(THIRD_PARTY_OAUTH_SUCCESS_EVENT, handleBroadcastedOAuthSuccess);
		return () => {
			window.removeEventListener(THIRD_PARTY_OAUTH_SUCCESS_EVENT, handleBroadcastedOAuthSuccess);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const trackEvent = (event: AnalyticsEventPayload) => {
		createAnalyticsEvent(event).fire(ANALYTICS_CHANNEL);
	};

	const handlePrimaryAction = () => {
		if (!integrationDetails || !integrationDetails.outboundAuthUrl) {
			return;
		}

		trackEvent(buildAuthClickedEventPayload(ThirdPartyAuthSource.EMPTY_STATE_CTA, tab));

		triggerThirdPartyAuthFlow(integrationDetails.outboundAuthUrl)
			.then(() => {
				setIntegrationDetails({
					...integrationDetails,
					isUserOAuthed: true,
					expirationDate: Date.now() + DEFAULT_EXPIRATION_FOR_3P_CONNECTION_STATUS,
				});
				trackEvent(buildAuthCompletedEventPayload(ThirdPartyAuthSource.EMPTY_STATE_CTA, tab));
			})
			.catch((_) => {
				trackEvent(buildAuthFailedEventPayload(ThirdPartyAuthSource.EMPTY_STATE_CTA, tab));
			});
	};

	const renderPrimaryAction = () => {
		if (hasAuthorized || !hasIntegrationDetails) {
			return null;
		}
		return (
			<Box xcss={styles.actionContainer}>
				<Button
					appearance="primary"
					testId={TEST_IDS.emptyStateNoResultsThirdPartyPrimaryAction}
					onClick={handlePrimaryAction}
				>
					{primaryAction}
				</Button>
			</Box>
		);
	};

	const renderDescription = () => {
		if (hasAuthorized) {
			return (
				<span data-testid={TEST_IDS.emptyStateNoResultsThirdPartyDescription}>{description}</span>
			);
		}
		return (
			<span data-testid={TEST_IDS.emptyStateNoResultsThirdPartyDescription}>
				{description}{' '}
				<Link
					href={DESCRIPTION_URL}
					target="_blank noreferrer"
					testId={TEST_IDS.emptyStateNoResultsThirdPartyDescriptionLink}
				>
					{descriptionLink}
				</Link>
			</span>
		);
	};

	return (
		<Box xcss={styles.emptyStateThirdPartyWrapperStyles}>
			{icon}
			<Box xcss={styles.headingContainer}>
				<Heading size="medium" as="h3">
					{header}
				</Heading>
			</Box>
			<Box xcss={styles.descriptionContainer}>
				<Text as="p">{renderDescription()}</Text>
			</Box>
			{renderPrimaryAction()}
		</Box>
	);
};

export const EmptyStateNoResultsThirdPartyWithAnalytics = withAnalyticsContext(
	EmptyStateNoResultsThirdParty,
);

export const renderGoogleDriveEmptyState = () => (
	<EmptyStateNoResultsThirdParty source={ThirdPartySource.GOOGLE_DRIVE} />
);

export const renderOneDriveEmptyState = () => (
	<EmptyStateNoResultsThirdParty source={ThirdPartySource.ONEDRIVE} />
);
